/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Offline Indicator Component
 * Monitors network connectivity and displays warning when offline
 *
 * Features:
 * - Online/offline status detection via centralized NetworkStatusService
 * - Reconnecting state indicator with progress
 * - Pending request count display
 * - Auto-dismiss notification when back online
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { WifiOff, RefreshCw, X, Wifi, Loader2, Clock } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { networkStatus } from "@/lib/network-status";

interface OfflineIndicatorProps {
  /** Show a toast notification when coming back online */
  showOnlineToast?: boolean;
  /** Custom message when offline */
  offlineMessage?: string;
  /** Position of the indicator */
  position?: "top" | "bottom";
  /** Callback when online status changes */
  onStatusChange?: (isOnline: boolean) => void;
  /** Number of pending requests to display (from network resilience) */
  pendingRequestCount?: number;
  /** Whether reconnection is in progress */
  isReconnecting?: boolean;
  /** Progress of queue processing (0-100) */
  processingProgress?: number;
  /** Callback to clear pending requests */
  onClearPending?: () => void;
}

export function OfflineIndicator({
  showOnlineToast = true,
  offlineMessage = "You're currently offline. Some features may be unavailable.",
  position = "bottom",
  onStatusChange,
  pendingRequestCount = 0,
  isReconnecting = false,
  processingProgress = 0,
  onClearPending,
}: OfflineIndicatorProps) {
  const [isOnline, setIsOnline] = useState(networkStatus.isOnline());
  const [isDismissed, setIsDismissed] = useState(false);
  const [showOnlineNotification, setShowOnlineNotification] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const prevOnlineRef = useRef(isOnline);
  const onlineToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Subscribe to centralized network status service
  useEffect(() => {
    const unsubscribe = networkStatus.subscribe((status) => {
      const wasOnline = prevOnlineRef.current;
      const nowOnline = status.isOnline;

      setIsOnline(nowOnline);
      prevOnlineRef.current = nowOnline;

      // Went from offline to online
      if (!wasOnline && nowOnline) {
        setIsDismissed(false);
        if (showOnlineToast) {
          setShowOnlineNotification(true);
          // Replace any in-flight auto-hide timer so rapid online/offline
          // flapping cannot stack timers, and so the timer can be cleared on
          // unmount (see cleanup below).
          if (onlineToastTimerRef.current) {
            clearTimeout(onlineToastTimerRef.current);
          }
          onlineToastTimerRef.current = setTimeout(
            () => setShowOnlineNotification(false),
            3000,
          );
        }
        onStatusChange?.(true);
      }

      // Went from online to offline
      if (wasOnline && !nowOnline) {
        setIsDismissed(false);
        onStatusChange?.(false);
      }
    });

    return () => {
      unsubscribe();
      if (onlineToastTimerRef.current) {
        clearTimeout(onlineToastTimerRef.current);
      }
    };
  }, [showOnlineToast, onStatusChange]);

  // Retry connection check using centralized service
  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    await networkStatus.checkConnectivity();
    setIsRetrying(false);
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  const positionClasses =
    position === "top"
      ? "fixed top-0 left-0 right-0 z-50"
      : "fixed bottom-0 left-0 right-0 z-50";

  // Don't render if online and no notification to show and not reconnecting
  if (isOnline && !showOnlineNotification && !isReconnecting) {
    return null;
  }

  // Don't render offline banner if dismissed (unless reconnecting)
  if (!isOnline && isDismissed && !isReconnecting) {
    return null;
  }

  // Reconnecting state (processing pending requests)
  if (isReconnecting && isOnline) {
    return (
      <div
        className={`${positionClasses} p-4`}
        data-testid="reconnecting-indicator"
      >
        <Alert className="max-w-lg mx-auto bg-blue-50 border-blue-300 shadow-lg">
          <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
          <AlertTitle className="text-blue-800 font-semibold">
            Reconnecting...
          </AlertTitle>
          <AlertDescription className="text-blue-700 mt-1">
            <p>
              Processing {pendingRequestCount} pending request
              {pendingRequestCount !== 1 ? "s" : ""}.
            </p>
            {processingProgress > 0 && (
              <div className="mt-2">
                <Progress value={processingProgress} className="h-2" />
                <p className="text-xs text-blue-600 mt-1">
                  {processingProgress}% complete
                </p>
              </div>
            )}
          </AlertDescription>
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 h-6 w-6 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
            onClick={() => setShowOnlineNotification(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </Alert>
      </div>
    );
  }

  // Online notification
  if (isOnline && showOnlineNotification) {
    return (
      <div className={`${positionClasses} p-4`}>
        <Alert className="max-w-md mx-auto bg-green-50 border-green-200 shadow-lg animate-slide-up">
          <Wifi className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">Back Online</AlertTitle>
          <AlertDescription className="text-green-700">
            Your connection has been restored.
          </AlertDescription>
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 h-6 w-6 p-0 text-green-600 hover:text-green-800 hover:bg-green-100"
            onClick={() => setShowOnlineNotification(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </Alert>
      </div>
    );
  }

  // Offline indicator
  return (
    <div className={`${positionClasses} p-4`}>
      <Alert className="max-w-lg mx-auto bg-amber-50 border-amber-300 shadow-lg">
        <WifiOff className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800 font-semibold">
          No Internet Connection
        </AlertTitle>
        <AlertDescription className="text-amber-700 mt-1">
          <p>{offlineMessage}</p>

          {/* Show pending requests count */}
          {pendingRequestCount > 0 && (
            <div className="flex items-center gap-1 mt-2 text-sm text-amber-600">
              <Clock className="h-3 w-3" />
              <span>
                {pendingRequestCount} request
                {pendingRequestCount !== 1 ? "s" : ""} queued
              </span>
              {onClearPending && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearPending}
                  className="h-5 px-2 text-xs text-amber-600 hover:text-amber-800 hover:bg-amber-100"
                >
                  Clear
                </Button>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
              disabled={isRetrying}
              className="border-amber-400 text-amber-800 hover:bg-amber-100"
            >
              {isRetrying ? (
                <>
                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="text-amber-700 hover:bg-amber-100"
            >
              Dismiss
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

export default OfflineIndicator;
